import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useOneWayIn from '../../hooks/use-one-way-in';
import { RaskAvklaringViser } from '@innovationnorway/kunde-raskavklaring-visning';
import { Feltgruppe, GruppertSkjema, Kontakt } from 'src/api/v2';
import { useTranslation } from 'react-i18next';
import { Notification } from '@innovationnorway/component-library';
import { PageParams } from '../types/PageParams';
import generateGuid from 'src/utils/Guid';

const Opportunity: React.FC = () => {
  const { formId } = useParams<PageParams>();
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const { oneWayInGroupedFormWOEnrollmentsQuery, oneWayInGroupedFormEnrollment } = useOneWayIn(formId);

  const form = oneWayInGroupedFormWOEnrollmentsQuery.data;
  const enrollment = oneWayInGroupedFormEnrollment.data;

  // Filter out previous applications and roles to hide it from RaskAvklaringViser because this is moved to CompanyInfo
  const groupsWithoutApplicationsAndRoles = useMemo(() => {
    if (!form?.grupper) {
      return [];
    }

    // Create a deep copy of the form to avoid mutating the original object that is cached in the query, because it is used in CompanyInfo
    const formCopy = JSON.parse(JSON.stringify(form)) as GruppertSkjema;
    if (!formCopy.grupper) {
      return [];
    }

    const groups = formCopy.grupper.filter((group) => group.navn !== 'Tidligere søknader') as Feltgruppe[];
    if (enrollment) groups.push(enrollment);

    const aboutField = groups.find((group) => group.navn === 'Om bedriften');
    if (aboutField?.felter) {
      aboutField.felter = aboutField.felter.filter((field) => field.navn !== 'Roller i bedriften');
    }

    return groups;
  }, [enrollment, form]);

  const formContact = React.useMemo(() => {
    if (!form?.kontakt) {
      return {
        navn: '',
        fornavn: '',
        etternavn: '',
        epost: '',
        telefon: '',
      } satisfies Kontakt;
    }

    return form?.kontakt;
  }, [form]);

  if (oneWayInGroupedFormWOEnrollmentsQuery.isError) {
    return (
      <Notification type="error" wrapperRole="alert">
        {tOneWayIn('errorCouldNotGetForm')}
      </Notification>
    );
  }

  if (form === undefined) {
    return <></>;
  }

  return (
    <RaskAvklaringViser
      id={form.id || generateGuid()}
      tittel={form.tittel || ''}
      grupper={groupsWithoutApplicationsAndRoles || []}
      organisasjon={form.organisasjon}
      kontakt={formContact}
      innsendtDato={form.innsendtDato}
    />
  );
};

export default Opportunity;
